import React from 'react';
import CustomTabs from '@/components/CustomTabs';
import { Collapse, Divider, Empty, Table, Tag } from 'antd';
import AirFranceProductTab from './AirFranceProductTab';
import { CustomTitle } from '@/components';
import { CheckOutlined } from '@ant-design/icons';
import { normalizeValue, ProductGroup } from './LufthansaCapabilities';
import type { ColumnsType } from 'antd/es/table';

type TableRow = { name: string } & Record<
  string,
  boolean | string | number | undefined
>;
/* ------------------------- TYPES ------------------------- */

type SimpleDays = Record<string, string>;
type MatrixDays = Record<string, Record<string, string>>;
type GroupedDays = Record<string, string>;

type OperationSection =
  | { type?: 'simple'; days?: SimpleDays }
  | { type?: 'matrix'; columns?: string[]; days?: MatrixDays }
  | { type?: 'grouped'; label?: string; days?: GroupedDays };

type OperationHours = Record<string, OperationSection>;

interface AirFranceTabsProps {
  operationHours?: OperationHours;
  products?: ProductGroup[];
  facilities?: Facilities;
}

/* --------------------------------------------------------- */
export interface WarehouseItem {
  key: string;
  value: boolean;
}

export interface RowItem {
  name: string;
  warehouses: WarehouseItem[];
}

export interface Facilities {
  columns: string[]; // Dynamic column names (e.g., ["Dl", "Afkl"])
  rows: RowItem[]; // Each row with warehouse key/value pairs
}

export function normalizeFacilities(
  rows: RowItem[] | undefined,
  columns: string[] | undefined
) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const safeColumns = Array.isArray(columns) ? columns : [];

  return safeRows.map((row) => {
    const map: Record<string, boolean> = {};

    // warehouses might also be undefined -> safe fallback
    const warehouses = Array.isArray(row.warehouses) ? row.warehouses : [];

    for (const wh of warehouses) {
      if (map[wh.key] === undefined) {
        map[wh.key] = wh.value;
      }
    }

    // ensure all expected columns exist
    safeColumns.forEach((col) => {
      if (map[col] === undefined) {
        map[col] = false;
      }
    });

    return {
      name: row.name ?? '',
      ...map,
    };
  });
}

export const isOpenNow = (day: string, timeRange: string): boolean => {
  if (!timeRange) return false;
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
  if (day !== today) return false;
  const str = timeRange.trim().toLowerCase();
  /* ---- SPECIAL CASES ---- */
  // 24/7 formats
  if (
    str === '24/7' ||
    str === '24x7' ||
    str === '24hrs' ||
    str === '24 hours'
  ) {
    return true;
  }
  // Closed formats
  if (['closed', 'close', 'n/a', 'na', '-'].includes(str)) {
    return false;
  }
  /* ---- NORMALIZE RANGE ---- */
  // normalize multiple dashes → single dash
  const cleaned = str.replace(/--+/g, '-').replace(/\s*-\s*/g, '-');
  const [startStr, endStr] = cleaned.split('-');
  if (!startStr || !endStr) return false;
  /* ---- PARSE HH:mm ---- */
  const parseTime = (t: string) => {
    const match = t.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return null;
    const [, h, m] = match.map(Number);
    return h * 60 + m;
  };
  const start = parseTime(startStr);
  let end = parseTime(endStr);
  if (start == null || end == null) return false;
  // Support end = 24:00
  if (endStr === '24:00') end = 24 * 60;
  const now = new Date();
  const current = now.getHours() * 60 + now.getMinutes();
  /* ---- SAME-DAY RANGE (normal) ---- */
  if (start < end) {
    return current >= start && current < end;
  }
  /* ---- OVERNIGHT RANGE (e.g. 22:00-05:00) ---- */
  return current >= start || current < end;
};

const AirFranceTabs: React.FC<AirFranceTabsProps> = ({
  operationHours,
  products,
  facilities,
}) => {
  /* ----------------- OPEN NOW LOGIC ----------------- */

  /* ----------------- RENDERERS ----------------- */

  const renderSimpleHours = (days: SimpleDays) => (
    <div className='hours-simple'>
      {Object.entries(days).map(([day, time]) => (
        <div className='row' key={day}>
          <div className='day'>{day}</div>
          <div className='zl-badge-item'>
            <time className='list-item'>{time}</time>
            {isOpenNow(day, time) && (
              <Tag
                style={{
                  backgroundColor: 'var(--thin-green)',
                  borderColor: 'var(--thin-green)',
                  color: 'var(--green-900)',
                }}
              >
                Open now
              </Tag>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const renderMatrixHours = (days: MatrixDays, columns: string[]) => (
    <div className='hours-matrix'>
      <div className='matrix-header'>
        <div></div>
        {columns.map((col) => (
          <div key={col} className='col-header'>
            {col}
          </div>
        ))}
      </div>

      {Object.entries(days).map(([day, values]) => (
        <div className='matrix-row' key={day}>
          <div className='day'>{day}</div>
          {columns.map((col) => (
            <div key={col} className='time'>
              {values[col]}
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  const renderGroupedHours = (days: GroupedDays, label: string) => (
    <div className='hours-grouped'>
      <div className='group-wrapper'>
        <div className='group-row'>
          <div className='group-label'></div>
          <div className='group-label'>{label}</div>
        </div>
      </div>

      {Object.entries(days).map(([day, time]) => (
        <div className='row' key={day}>
          <div className='day'>{day}</div>
          <div className='time'>{time}</div>
        </div>
      ))}
    </div>
  );

  const renderOperationSection = (section: OperationSection) => {
    switch (section.type) {
      case 'simple':
        return renderSimpleHours(section.days);
      case 'matrix':
        return renderMatrixHours(section.days, section.columns);
      case 'grouped':
        return renderGroupedHours(section.days, section.label);
      default:
        return null;
    }
  };

  /* ----------------- LABEL FORMATTER ----------------- */
  const formatLabel = (key: string) => {
    // If key already contains spaces OR is uppercase, return as is
    if (key.includes(' ') || key === key.toUpperCase()) return key.trim();

    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (c) => c.toUpperCase())
      .trim();
  };

  /* ----------------- BUILD COLLAPSE ITEMS ----------------- */
  const buildCollapseItems = (data?: OperationHours) =>
    Object.entries(data || ({} as OperationHours)).map(
      ([key, section], idx) => ({
        key: String(idx),
        label: formatLabel(key),
        children: renderOperationSection(section),
      })
    );

  const warehouseItems = buildCollapseItems(
    operationHours?.warehouseOperationHours as OperationHours
  );

  const customItems = buildCollapseItems(
    operationHours?.customOperationHours as OperationHours
  );

  const dataSource = normalizeFacilities(
    facilities?.rows || [],
    facilities?.columns || []
  );
  const safeColumns = Array.isArray(facilities?.columns)
    ? facilities.columns
    : [];

  const tableColumns: ColumnsType<TableRow> = [
    {
      title: 'Pharma',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: 250,
    },
    ...safeColumns.map((col) => ({
      title: col,
      dataIndex: col,
      key: col,
      width: 120,
      align: 'center' as const,
      render: (value: string | boolean | number | undefined) => {
        const val = normalizeValue(value);
        return val === true ? <CheckOutlined /> : val || '-';
      },
    })),
  ];
  /* ----------------- PAGE TABS ----------------- */
  const pageTabs = [
    {
      key: '1',
      label: 'Product availability & servicing',
      children: (
        <>
          <div className='zl-heading-16 mb-3'>Products Available</div>
          {products?.length > 0 ? (
            <AirFranceProductTab products={products} />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </>
      ),
    },
    {
      key: '2',
      label: 'Warehouses & facilities',
      children: (
        <>
          <div className='zl-heading-16 mb-3'>Operating hours</div>
          {warehouseItems?.length > 0 ? (
            <Collapse
              className='zl-custom-collapse Lufthansa-tabs'
              items={warehouseItems}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
          <Divider />
          {/* Speed  */}
          <CustomTitle
            text='Warehouse facilities'
            color='var(--text-primary)'
            fontSize='16px'
            lineHeight='20px'
            fontWeight='600'
            marginBottom={12}
          />
          <Table
            className='zl-custom-times-table'
            dataSource={dataSource}
            columns={tableColumns}
            pagination={false}
            rowHoverable={false}
            bordered
          />
        </>
      ),
    },
    {
      key: '3',
      label: 'Customs & Claims',
      children: (
        <>
          <div className='zl-heading-16 mb-3'>Operating hours</div>
          {customItems?.length > 0 ? (
            <Collapse
              className='zl-custom-collapse Lufthansa-tabs'
              items={customItems}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </>
      ),
    },
  ];

  /* ----------------- RENDER ----------------- */

  return (
    <div>
      <div className='zl-custom-tabs' style={{ marginTop: '10px' }}>
        <CustomTabs items={pageTabs} />
      </div>
    </div>
  );
};

export default AirFranceTabs;
